import type { AppNotification, NotificationChannel, NotificationDigest, NotificationPreference, NotificationSeverity } from '@/types/api'

/**
 * Maps the app deep links stored on notifications (`signetix://displays/<id>`, MOBILE.md §6)
 * to web admin paths. Unknown links open nothing.
 */
export function notificationPath(url: string | null | undefined, type?: string | null): string | null {
  // Phase F: Signetix Flow notifications without a deep link still open the flows page.
  if (!url && (type === 'flow.notification' || type === 'flow.failed')) return '/flows'
  if (!url || !url.startsWith('signetix://')) return null
  const [resource, id] = url.slice('signetix://'.length).split('/')
  const safeId = id && /^[0-9a-z]{26}$/i.test(id) ? id.toLowerCase() : null
  switch (resource) {
    case 'displays':
    case 'campaigns':
    case 'slideshows':
    case 'data-sources':
    case 'players':
      return safeId ? `/${resource}/${safeId}` : `/${resource}`
    case 'schedules':
      return '/schedules'
    case 'flows':
      // `flow.notification` (a flow's notify action) and `flow.failed` open the flow.
      return safeId ? `/flows/${safeId}` : '/flows'
    case 'flow-executions':
      return '/flows'
    case 'content-slot-items':
      // Phase E: approvers land on the queue, submitters on their slot content.
      return type === 'content_slot.reviewed' ? '/local-content' : '/approvals/slots'
    case 'local-content':
      return '/local-content'
    case 'emergency':
      return '/emergency'
    case 'webhooks':
      return '/settings/webhooks'
    case 'subscription':
      return '/settings/subscription'
    default:
      return null
  }
}

/** Badge text for the bell: hidden at 0, capped at 99+. */
export function unreadBadge(count: number | undefined): string | null {
  if (!count || count <= 0) return null
  return count > 99 ? '99+' : String(count)
}

export const SEVERITY_TONE: Record<NotificationSeverity, 'secondary' | 'warning' | 'danger'> = {
  info: 'secondary',
  warning: 'warning',
  error: 'danger',
  critical: 'danger',
}

export const CHANNEL_LABELS: Record<NotificationChannel, string> = { in_app: 'In-app', push: 'Mobile push', mail: 'Email' }

export const DIGEST_LABELS: Record<NotificationDigest, string> = {
  immediate: 'Immediately',
  hourly: 'Hourly digest',
  daily: 'Daily digest',
  off: 'No email',
}

/** Preferences grouped for display, preserving server order inside each group. */
export function groupPreferences(prefs: NotificationPreference[]): { group: string; items: NotificationPreference[] }[] {
  const groups = new Map<string, NotificationPreference[]>()
  for (const pref of prefs) {
    const list = groups.get(pref.group) ?? []
    list.push(pref)
    groups.set(pref.group, list)
  }
  return [...groups.entries()].map(([group, items]) => ({ group, items }))
}

/** Channels the server can deliver (older servers omit `mail`). */
export function availableChannels(pref: NotificationPreference): NotificationChannel[] {
  return (['in_app', 'push', 'mail'] as const).filter((c) => pref.available_channels.includes(c))
}

/** Optimistically mark one / all notifications as read in a cached page. */
export function markRead(list: AppNotification[], id: string | 'all', at = new Date().toISOString()): AppNotification[] {
  return list.map((n) => (n.read_at === null && (id === 'all' || n.id === id) ? { ...n, read_at: at } : n))
}
